import { CheckCircleOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { Alert, Col, Divider, Row } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Spin from "../../components/general/antd/spin";
import PageHeader from "../../components/general/layout/page-header";
import Card from "../../components/home/cards";
import EndPoints from "../../services/end-points";
import Chart from "../../components/general/charts/pie";
import StoreProductStats from "../../components/home/store-product-stats";
import {
  IOrderOperationsAnalytics,
  IOrderOperationsAnalyticsQuery,
  IStatistics,
} from "../../models/statistcs/respons";
import { IApiLoadState } from "../../models/statistcs/cache";
import RefreshBtn from "../../components/general/table-components/actions/refresh-btn";
import AreaClientStats from "../../components/home/area-client-stats";
import AreaDeliveryTimeStats from "../../components/home/area-delivery-time-stats";
import PreparingTimeStats from "../../components/home/preparing-time-stats";
import OrderCycleTimeStats from "../../components/home/order-cycle-time-stats";
import StoreAcceptanceTimeStats from "../../components/home/store-acceptance-time-stats";
import ModifiedOrderAcceptanceTimeStats from "../../components/home/modified-order-acceptance-time-stats";
import AccountingFinalizationTimeStats from "../../components/home/accounting-finalization-time-stats";
import DriverSearchTimeStats from "../../components/home/driver-search-time-stats";
import CustomerCancellationStats from "../../components/home/customer-cancellation-stats";
import AdministrativeCancellationStats from "../../components/home/administrative-cancellation-stats";
import OrderOperationsAnalytics from "../../components/home/order-operations-analytics";
import { mapOrderOperationsAnalytics } from "../../utils/order-operations-analytics";
import CustomerSatisfactionStats from "../../components/home/customer-satisfaction-stats";
import getApiErrorMessage from "../../utils/api/get-api-error-message";
interface IProps {}

const Home: React.FC<IProps> = (props) => {
  const { t } = useTranslation();

  const [statistics, setStatistics] = useState<IStatistics>();
  const [statisticsError, setStatisticsError] = useState<string>();
  const [orderOperationsState, setOrderOperationsState] = useState<
    IApiLoadState<IOrderOperationsAnalytics>
  >({ status: "loading" });
  const [orderOperationsQuery, setOrderOperationsQuery] =
    useState<IOrderOperationsAnalyticsQuery>({});
  const [statisticsLoading, setStatisticsLoading] = useState(true);
  const orderOperationsPollingRef = useRef<number>();
  const orderOperationsRequestRef = useRef(0);
  const orderOperationsCacheRef = useRef<
    Record<string, IOrderOperationsAnalytics>
  >({});
  const areaClientStats = statistics?.AreaClientStats;
  const orderOperationsAnalytics = orderOperationsState.data;
  const orderOperationsLoading = orderOperationsState.status === "loading";
  const orderOperationsStats = useMemo(
    () => mapOrderOperationsAnalytics(orderOperationsAnalytics),
    [orderOperationsAnalytics],
  );
  const allClientCount =
    areaClientStats?.totalClientCount ?? statistics?.ClientCount ?? 0;
  const realClientCount = areaClientStats?.realTotalClientCount ?? 0;

  const getStatisticsErrorMessage = useCallback(
    (error: any) => {
      const statusCode = error?.response?.status;

      if (statusCode === 401) return t("statistics_session_expired");
      if (statusCode === 403) return t("statistics_permission_denied");
      if (statusCode === 422) {
        return getApiErrorMessage(error, t("statistics_invalid_filters"));
      }

      return getApiErrorMessage(error, t("server_error_try_again"));
    },
    [t],
  );

  const clearOrderOperationsPolling = useCallback(() => {
    if (orderOperationsPollingRef.current) {
      window.clearTimeout(orderOperationsPollingRef.current);
      orderOperationsPollingRef.current = undefined;
    }
  }, []);

  const loadGeneralStatistics = useCallback(async () => {
    try {
      setStatisticsLoading(true);
      setStatisticsError(undefined);
      const { data } = await EndPoints.statistics.getAllStatistics({});
      setStatistics(data);
    } catch (error) {
      setStatisticsError(getStatisticsErrorMessage(error));
    } finally {
      setStatisticsLoading(false);
    }
  }, [getStatisticsErrorMessage]);

  const loadOrderOperationsAnalytics = useCallback(
    async (
      analyticsQuery: IOrderOperationsAnalyticsQuery = {},
      showLoading = true,
    ) => {
      const requestId = orderOperationsRequestRef.current + 1;
      orderOperationsRequestRef.current = requestId;
      clearOrderOperationsPolling();
      setOrderOperationsQuery(analyticsQuery);

      if (showLoading) {
        setOrderOperationsState((previous) => ({
          status: "loading",
          data: previous.data,
          cacheMeta: previous.cacheMeta,
        }));
      }

      try {
        const { data } =
          await EndPoints.statistics.getOrderOperationsAnalytics(
            analyticsQuery,
          );

        if (requestId !== orderOperationsRequestRef.current) return;

        if (data.pending && data.cacheMeta?.status === "pending") {
          const cachedData = data.cacheMeta.requestHash
            ? orderOperationsCacheRef.current[data.cacheMeta.requestHash]
            : undefined;

          setOrderOperationsState((previous) => ({
            status: "pending",
            data: cachedData || previous.data || data,
            cacheMeta: data.cacheMeta,
          }));

          const waitSeconds = Math.max(
            data.cacheMeta.retryAfterSeconds ?? 10,
            1,
          );
          orderOperationsPollingRef.current = window.setTimeout(() => {
            loadOrderOperationsAnalytics(analyticsQuery, false);
          }, waitSeconds * 1000);

          return;
        }

        if (data.cacheMeta?.requestHash) {
          orderOperationsCacheRef.current[data.cacheMeta.requestHash] = data;
        }

        setOrderOperationsState({
          status: "ready",
          data,
          cacheMeta: data.cacheMeta,
        });
      } catch (error) {
        if (requestId !== orderOperationsRequestRef.current) return;

        setOrderOperationsState((previous) => ({
          status: "error",
          data: previous.data,
          cacheMeta: previous.cacheMeta,
          statusCode: error?.response?.status,
          message: getStatisticsErrorMessage(error),
        }));
      }
    },
    [clearOrderOperationsPolling, getStatisticsErrorMessage],
  );

  const refreshOrderOperationsAnalytics = async (
    query?: IOrderOperationsAnalyticsQuery,
  ) => {
    await loadOrderOperationsAnalytics(query || {});
  };

  useEffect(() => {
    loadGeneralStatistics();
    loadOrderOperationsAnalytics({});

    return () => {
      clearOrderOperationsPolling();
      orderOperationsRequestRef.current += 1;
    };
  }, [
    clearOrderOperationsPolling,
    loadGeneralStatistics,
    loadOrderOperationsAnalytics,
  ]);

  return (
    <>
      <PageHeader
        title={t("home_title")}
        extra={[
          <RefreshBtn
            key={0}
            loading={statisticsLoading}
            onClick={loadGeneralStatistics}
          >
            {t("refresh")}
          </RefreshBtn>,
        ]}
      />
      {statisticsError && (
        <Alert
          style={{ marginBottom: 12 }}
          type="error"
          showIcon
          message={statisticsError}
        />
      )}
      {statisticsLoading && !statistics ? (
        <Spin />
      ) : (
        <>
          <Row gutter={[8, 8]}>
            <Col xs={24} md={12} xl={8}>
              <Card
                toolTip={t("ClientCount")}
                title={t("ClientCount")}
                icon={<UsergroupAddOutlined />}
                count={allClientCount}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Card
                toolTip={t("real_client_count")}
                title={t("real_client_count")}
                icon={<CheckCircleOutlined />}
                count={realClientCount}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Card
                toolTip={t("DeliveryCount")}
                title={t("DeliveryCount")}
                icon={<UsergroupAddOutlined />}
                count={statistics?.DeliveryCount}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Card
                toolTip={t("DeliveryCountWork")}
                title={t("DeliveryCountWork")}
                icon={<UsergroupAddOutlined />}
                count={statistics?.DeliveryCountWork}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Card
                toolTip={t("OrderCount")}
                title={t("OrderCount")}
                icon={<UsergroupAddOutlined />}
                count={statistics?.OrderCount}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Card
                toolTip={t("OrderInProgressCount")}
                title={t("OrderInProgressCount")}
                icon={<UsergroupAddOutlined />}
                count={statistics?.OrderInProgressCount}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Card
                toolTip={t("StoreCount")}
                title={t("StoreCount")}
                icon={<UsergroupAddOutlined />}
                count={statistics?.StoreCount}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Card
                toolTip={t("StoreActiveCount")}
                title={t("StoreActiveCount")}
                icon={<UsergroupAddOutlined />}
                count={statistics?.StoreActiveCount}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Card
                toolTip={t("PharmacistsCount")}
                title={t("PharmacistsCount")}
                icon={<UsergroupAddOutlined />}
                count={statistics?.PharmacistsCount}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Card
                toolTip={t("PharmacistsActiveCount")}
                title={t("PharmacistsActiveCount")}
                icon={<UsergroupAddOutlined />}
                count={statistics?.PharmacistsActiveCount}
              />
            </Col>
          </Row>
          <Divider orientation="center">{t("statistics")}</Divider>
          <Row gutter={[28, 28]}>
            <Col xs={24} md={12} lg={8}>
              <Chart
                lables={[t("DeliveryCount"), t("DeliveryCountWork")]}
                count={[
                  statistics?.DeliveryCount,
                  statistics?.DeliveryCountWork,
                ]}
              />
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Chart
                lables={[t("OrderCount"), t("OrderInProgressCount")]}
                count={[
                  statistics?.OrderCount,
                  statistics?.OrderInProgressCount,
                ]}
              />
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Chart
                lables={[t("StoreCount"), t("StoreActiveCount")]}
                count={[statistics?.StoreCount, statistics?.StoreActiveCount]}
              />
            </Col>
          </Row>
          <AreaClientStats stats={areaClientStats} />
          <StoreProductStats stats={statistics?.StoreProductStats} />
          <OrderOperationsAnalytics
            analytics={orderOperationsAnalytics}
            loading={orderOperationsLoading}
            status={orderOperationsState.status}
            cacheMeta={orderOperationsState.cacheMeta}
            errorMessage={orderOperationsState.message}
            onRefresh={refreshOrderOperationsAnalytics}
          >
            <StoreAcceptanceTimeStats
              stats={orderOperationsStats.storeAcceptance}
              onChange={() => undefined}
              hideDateControls
            />
            <PreparingTimeStats
              stats={orderOperationsStats.preparing}
              onChange={() => undefined}
              hideDateControls
            />
            <ModifiedOrderAcceptanceTimeStats
              stats={orderOperationsStats.customerAcceptance}
              onChange={() => undefined}
              hideDateControls
            />
            <AccountingFinalizationTimeStats
              stats={orderOperationsStats.accountingFinalization}
              onChange={() => undefined}
              hideDateControls
            />
            <DriverSearchTimeStats
              stats={orderOperationsStats.driverSearch}
              onChange={() => undefined}
              hideDateControls
            />
            <AreaDeliveryTimeStats
              stats={orderOperationsStats.delivery}
              onChange={() => undefined}
              hideDateControls
            />
            <OrderCycleTimeStats
              stats={orderOperationsStats.orderCycle}
              onChange={() => undefined}
              hideDateControls
            />
            <CustomerCancellationStats
              stats={orderOperationsStats.customerCancellation}
              onChange={() => undefined}
              hideDateControls
            />
            <AdministrativeCancellationStats
              stats={orderOperationsStats.administrativeCancellation}
              onChange={() => undefined}
              hideDateControls
            />
          </OrderOperationsAnalytics>
          <CustomerSatisfactionStats />
        </>
      )}
    </>
  );
};

export default Home;
